import { useCallback, useEffect, useMemo, useState } from 'react'
import { Tracker } from '@/core/classes/Model'
import { RootState, useAppDispatch, useAppSelector } from '@/redux/rootReducer'

type RequestHandlerConfig<P> = {
  requestTrackerSelector: (state: RootState) => Tracker
  onSuccess: (dispatchedPayload: P | null) => void
  onError: (error: any, dispatchedPayload: P | null) => void
}

export type RequestHandlerDispatch = (action: { payload: any }) => any

export function useRequestHandler<P = any>({ onSuccess, onError, requestTrackerSelector }: RequestHandlerConfig<P>) {
  const dispatch = useAppDispatch()
  const [hasDispatchedAction, setHasDispatchedAction] = useState<boolean>(false)
  const [dispatchedPayload, setDispatchedPayload] = useState<P | null>(null)

  const requestTracker = useAppSelector(requestTrackerSelector)

  useEffect(() => {
    if (!hasDispatchedAction) return

    // success
    if (!requestTracker.loading && !requestTracker.error) {
      onSuccess(dispatchedPayload)
      setDispatchedPayload(null)
      setHasDispatchedAction(false)
    }
    // failure
    else if (!requestTracker.loading && requestTracker.error) {
      onError(requestTracker.error, dispatchedPayload)
      setDispatchedPayload(null)
      setHasDispatchedAction(false)
    } // else is loading do nothing
  }, [onSuccess, onError, dispatchedPayload, hasDispatchedAction, requestTracker])

  const handleDispatch: RequestHandlerDispatch = useCallback(
    (action) => {
      dispatch(action)
      setDispatchedPayload(action.payload)
      setHasDispatchedAction(true)
    },
    [dispatch]
  )

  const requestHandler = useMemo(
    () => ({
      dispatch: handleDispatch,
      requestTracker,
    }),
    [handleDispatch, requestTracker]
  )

  return requestHandler
}
